/**
 * Mock data for the Influencers page.
 *
 * A SEPARATE dataset from `affiliates.ts` — the real Influencers feed will come
 * either from its own endpoint or from the shared partners endpoint filtered to
 * the "influencer" kind. Either way the page never reads `affiliatesData`.
 *
 * Temporary — remove when wired to an API (see docs/mock-data.md). Stat/note
 * labels travel with their data row (an influencer's own metrics); static page
 * chrome lives in the component.
 */
import type { InfluencersData } from './types';

export const influencersData: InfluencersData = {
  header: { title: 'Influencers' },
  overview: [
    { id: 'total-spend', label: 'Total Spend', value: '$8,720', change: '+42%', changeType: 'increase' },
    { id: 'blended-cpa', label: 'Blended CPA', value: '$94', change: '+9%', changeType: 'increase' },
  ],
  count: 5,
  influencers: [
    {
      id: 'cryptoqueen',
      name: '@CryptoQueen',
      type: 'cpa',
      typeLabel: 'CPA',
      typeDetail: '$45 / FTD',
      stats: [
        { id: 'regs', label: 'Regs', value: '204' },
        { id: 'ftds', label: 'FTDs', value: '71' },
        { id: 'reg-ftd', label: 'Reg → FTD', value: '34.8%' },
        { id: 'avg-dep', label: 'Avg Dep', value: '$83' },
      ],
      terms: { dealType: 'cpa', cpaAmount: '45', trackingLink: 'https://track.fm/cryptoqueen' },
    },
    {
      id: 'progamertv',
      name: 'ProGamerTV',
      type: 'rev-share',
      typeLabel: 'Rev Share',
      typeDetail: '35% rev share',
      stats: [
        { id: 'regs', label: 'Regs', value: '412' },
        { id: 'ftds', label: 'FTDs', value: '124' },
        { id: 'reg-ftd', label: 'Reg → FTD', value: '30.1%' },
        { id: 'avg-dep', label: 'Avg Dep', value: '$61' },
        { id: 'cost-ftd', label: 'Cost / FTD', value: '$21.4' },
        { id: 'rev-ftd', label: 'Rev / FTD', value: '$94.2' },
        { id: 'roi', label: 'ROI', value: '340%', tone: 'positive' },
        { id: 'total-cost', label: 'Total Cost', value: '$2,654' },
      ],
      note: { text: 'Top performer. Consider increasing budget allocation', tone: 'success', icon: 'check' },
      terms: { dealType: 'rev-share', revShare: '35', trackingLink: 'https://track.fm/progamertv' },
    },
    {
      id: 'slotstreamerjoe',
      name: 'SlotStreamerJoe',
      type: 'cpa',
      typeLabel: 'CPA',
      typeDetail: '$50 / FTD',
      stats: [
        { id: 'regs', label: 'Regs', value: '88' },
        { id: 'ftds', label: 'FTDs', value: '9' },
        { id: 'reg-ftd', label: 'Reg → FTD', value: '10.2%' },
        { id: 'avg-dep', label: 'Avg Dep', value: '$28' },
        { id: 'cost-ftd', label: 'Cost / FTD', value: '$50' },
        { id: 'rev-ftd', label: 'Rev / FTD', value: '$38' },
        { id: 'roi', label: 'ROI', value: '-24%', tone: 'negative' },
        { id: 'total-cost', label: 'Total Cost', value: '$450' },
      ],
      note: { text: 'Low conversion & negative ROI. Review Influencer', tone: 'danger', icon: 'warning' },
      terms: { dealType: 'cpa', cpaAmount: '50', trackingLink: 'https://track.fm/slotstreamerjoe' },
    },
    {
      id: 'fitnessbymaria',
      name: 'FitnessByMaria',
      type: 'rev-share',
      typeLabel: 'Rev Share',
      typeDetail: '28% rev share',
      stats: [
        { id: 'regs', label: 'Regs', value: '256' },
        { id: 'ftds', label: 'FTDs', value: '78' },
        { id: 'reg-ftd', label: 'Reg → FTD', value: '30.5%' },
        { id: 'avg-dep', label: 'Avg Dep', value: '$69' },
        { id: 'cost-ftd', label: 'Cost / FTD', value: '$19.3' },
        { id: 'rev-ftd', label: 'Rev / FTD', value: '$96.6' },
        { id: 'roi', label: 'ROI', value: '320%', tone: 'positive' },
        { id: 'total-cost', label: 'Total Cost', value: '$1,505' },
      ],
      terms: { dealType: 'rev-share', revShare: '28', trackingLink: 'https://track.fm/fitnessbymaria' },
    },
    {
      id: 'techreviewdan',
      name: 'TechReviewDan',
      type: 'hybrid',
      typeLabel: 'Hybrid',
      typeDetail: '$25 CPA + 25% Rev Share + ',
      typeDetailAccent: '$12,000 fixed',
      stats: [
        { id: 'regs', label: 'Regs', value: '221' },
        { id: 'ftds', label: 'FTDs', value: '70' },
        { id: 'reg-ftd', label: 'Reg → FTD', value: '31.7%' },
        { id: 'avg-dep', label: 'Avg Dep', value: '$67' },
        { id: 'true-cost-ftd', label: 'True Cost / FTD', value: '$358.4', tone: 'accent' },
        { id: 'rev-ftd', label: 'Rev / FTD', value: '$93.1' },
        { id: 'roi', label: 'ROI', value: '-65%', tone: 'negative' },
        { id: 'total-cost', label: 'Total Cost', value: '$1,750' },
      ],
      note: { text: 'Fixed cost adds $314.3/FTD — 11 video posts pending', tone: 'accent', icon: 'sparkle' },
      terms: {
        dealType: 'hybrid',
        cpaAmount: '25',
        revShare: '25',
        trackingLink: 'https://track.fm/techreviewdan',
        fixedCosts: '12000',
        fixedCostNote: 'Sponsorship fee',
      },
    },
  ],
};

export default influencersData;
